import React from 'react'
import { useDispatch } from 'react-redux'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiBriefcase } from '@react-icons/all-files/fi/FiBriefcase'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiClock } from '@react-icons/all-files/fi/FiClock'
import { FiLayers } from '@react-icons/all-files/fi/FiLayers'
import { FiMail } from '@react-icons/all-files/fi/FiMail'
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiRadio } from '@react-icons/all-files/fi/FiRadio'
import { FiSearch } from '@react-icons/all-files/fi/FiSearch'
import { FiSun } from '@react-icons/all-files/fi/FiSun'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { FiVolume2 } from '@react-icons/all-files/fi/FiVolume2'
import { SHOW_POPUP } from '../../../store/slices/ui'
import { panelSet } from '../../../store/slices/chatV2'
import * as gtag from '../../../lib/gtag'
import ProductionPopup from '../../DeferredProductionPopup'
import { useOrderFlow } from '../../unified-order/OrderFlowProvider'
import PageShell from '../foundations/PageShell'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import ServiceHero from './ServiceHero'
import ReviewProof from './ReviewProof'
import FAQGroup from './FAQGroup'
import CorporateProposalForm from './CorporateProposalForm'
import CorporateReadiness from './CorporateReadiness'
import styles from './CorporateProductionPage.module.scss'

const capabilityIcons = {
  audio: FiVolume2,
  video: FiMonitor,
  lighting: FiSun,
  staging: FiLayers,
  crew: FiUsers,
  streaming: FiRadio
}

const eventTypeIcons = [FiUsers, FiBriefcase, FiLayers, FiMonitor, FiVolume2, FiCalendar]

const SectionHeading = ({ eyebrow, title, description, inverse = false, id }) => (
  <div className={`${styles.sectionHeading} ${inverse ? styles.inverseHeading : ''}`.trim()}>
    {eyebrow && <span>{eyebrow}</span>}
    <h2 id={id}>{title}</h2>
    {description && <p>{description}</p>}
  </div>
)

const CorporateProductionPage = ({ content, reviewData, totalEvents }) => {
  const dispatch = useDispatch()
  const { launchIntent } = useOrderFlow()

  const track = (action, value = 3) => gtag.event({
    action,
    label: 'leads',
    value,
    category: 'orders'
  })

  const openProposal = sourceSurface => {
    track('corporate_production_proposal_open', 15)
    launchIntent({ sourceSurface, fulfillment: 'delivery_setup' }, () => {
      dispatch(SHOW_POPUP({
        key: 'corporate-production-proposal',
        size: 'big',
        trapFocus: true,
        ariaLabelledBy: 'corporate-proposal-modal-title',
        body: (
          <PageShell className={styles.modalShell}>
            <div className={styles.modalForm}>
              <span>Corporate production</span>
              <h2 id='corporate-proposal-modal-title' tabIndex={-1} data-dialog-initial-focus>Tell us what you are planning</h2>
              <p>Share the details you already have. A producer will help define the technical scope and next step.</p>
              <CorporateProposalForm idPrefix='corporate-proposal-modal' compact />
            </div>
          </PageShell>
        )
      }))
    })
  }

  const openBuilder = sourceSurface => {
    track('corporate_event_builder_open')
    launchIntent({ sourceSurface, fulfillment: 'delivery_setup' }, () => {
      dispatch(SHOW_POPUP({ size: 'xl', key: 'prodpop', body: <ProductionPopup /> }))
    })
  }

  const openChat = () => {
    track('corporate_production_chat', 15)
    dispatch(panelSet(true))
    window.dispatchEvent(new CustomEvent('msr:open-chat'))
  }

  const rating = reviewData?.source?.averageRating
  const reviewCount = reviewData?.source?.totalReviewCount
  const proof = content.proof.map(item => {
    if (item.key === 'events' && totalEvents != null && Number.isFinite(Number(totalEvents))) {
      return { ...item, value: Number(totalEvents).toLocaleString('en-US') }
    }
    if (item.key === 'rating' && rating) return { ...item, value: `${rating} / 5` }
    if (item.key === 'reviews' && reviewCount) {
      return { ...item, value: Number(reviewCount).toLocaleString('en-US') }
    }
    return item
  })

  const processSteps = content.process.steps.map((step, index) => ({
    title: step.title,
    description: step.description,
    icon: [FiSearch, FiCalendar, FiClock, FiCheck][index]
  }))

  return (
    <PageShell className={styles.page}>
      <ServiceHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Corporate AV Production' }
        ]}
        eyebrow={content.hero.eyebrow}
        title={content.hero.title}
        description={content.hero.description}
        image={content.hero.image}
        imageAlt={content.hero.imageAlt}
        stackActionsOnMobile
        primaryAction={{
          label: content.hero.primaryAction,
          icon: FiMail,
          onClick: () => openProposal('corporate_production_hero'),
          channel: 'quote',
          conversionKey: 'corporate-event-production.hero.proposal',
          salesPath: 'managed'
        }}
        secondaryAction={{
          label: content.hero.secondaryAction,
          icon: FiPhone,
          href: 'tel:+13059744411',
          external: true,
          onClick: () => track('corporate_production_hero_call', 15),
          channel: 'phone',
          conversionKey: 'corporate-event-production.hero.call',
          salesPath: 'managed'
        }}
        processLabel='Corporate production process'
        steps={processSteps}
      />

      <section className={styles.proof} aria-label='Corporate production proof'>
        <Container className={styles.proofGrid}>
          {proof.map(item => (
            <div key={item.key}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </Container>
      </section>

      <section className={styles.paths} aria-labelledby='corporate-paths-title'>
        <Container>
          <SectionHeading id='corporate-paths-title' {...content.paths} />
          <div className={styles.pathGrid}>
            <article className={`${styles.pathCard} ${styles.managedPath}`}>
              <span>{content.paths.managed.label}</span>
              <h3>{content.paths.managed.title}</h3>
              <p>{content.paths.managed.description}</p>
              <Action size='large' icon={FiArrowRight} onClick={() => openProposal('corporate_production_path_choice')}>
                {content.paths.managed.action}
              </Action>
              <div className={styles.contactLinks} aria-label='Direct production contacts'>
                <Action variant='plain' href='tel:+13059744411' external icon={FiPhone} onClick={() => track('corporate_production_path_call', 15)}>Call</Action>
                <Action variant='plain' href='mailto:info@miamisoundrental.com' external icon={FiMail} onClick={() => track('corporate_production_path_email', 15)}>Email</Action>
                <Action variant='plain' icon={FiMessageCircle} onClick={openChat}>Chat</Action>
              </div>
            </article>
            <article className={styles.pathCard}>
              <span>{content.paths.planning.label}</span>
              <h3>{content.paths.planning.title}</h3>
              <p>{content.paths.planning.description}</p>
              <Action variant='secondary' size='large' icon={FiCalendar} onClick={() => openBuilder('corporate_production_path_builder')}>
                {content.paths.planning.action}
              </Action>
              <Action variant='plain' href='/#categories' icon={FiArrowRight} onClick={() => track('corporate_production_browse_rentals')}>
                Browse individual rental categories
              </Action>
            </article>
          </div>
        </Container>
      </section>

      <section className={styles.eventTypes} aria-labelledby='corporate-event-types-title'>
        <Container>
          <SectionHeading id='corporate-event-types-title' {...content.eventTypes} />
          <div className={styles.eventTypeGrid}>
            {content.eventTypes.items.map((item, index) => {
              const Icon = eventTypeIcons[index % eventTypeIcons.length]
              return (
                <article key={item.title}>
                  <Icon aria-hidden='true' />
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  {item.href && (
                    <Action variant='plain' href={item.href} icon={FiArrowRight}>
                      {item.action || 'Explore service'}
                    </Action>
                  )}
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <section className={styles.capabilities} aria-labelledby='corporate-capabilities-title'>
        <Container>
          <SectionHeading id='corporate-capabilities-title' inverse {...content.capabilities} />
          <div className={styles.capabilityGrid}>
            {content.capabilities.items.map(item => {
              const Icon = capabilityIcons[item.icon] || FiCheck
              return (
                <article key={item.title}>
                  <span><Icon aria-hidden='true' /></span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <CorporateReadiness
        content={content.procurement}
        headingId='corporate-procurement-title'
        variant='detailed'
        primaryAction={{
          label: content.procurement.primaryAction,
          onClick: () => openProposal('corporate_production_procurement'),
          dataConversionChannel: 'quote',
          dataConversionKey: 'corporate-event-production.procurement.proposal',
          dataConversionLocation: 'corporate-procurement',
          dataConversionSalesPath: 'managed'
        }}
        secondaryAction={{
          label: content.procurement.secondaryAction,
          href: 'mailto:info@miamisoundrental.com?subject=Corporate%20procurement%20requirements',
          external: true,
          icon: FiMail,
          onClick: () => track('corporate_production_procurement_email', 10),
          dataConversionChannel: 'email',
          dataConversionKey: 'corporate-event-production.procurement.email',
          dataConversionLocation: 'corporate-procurement',
          dataConversionSalesPath: 'managed'
        }}
      />

      <section className={styles.caseStudies} aria-labelledby='corporate-case-studies-title'>
        <Container>
          <SectionHeading
            id='corporate-case-studies-title'
            eyebrow='Documented production work'
            title='Real South Florida corporate productions'
            description='Review the venue, crew, service areas, and documented production scope—not generic claims or stock examples.'
          />
          <div className={styles.caseGrid}>
            {content.caseStudies.map(project => (
              <article className={styles.caseCard} key={project.slug}>
                <Media src={project.fallbackImage} alt={`${project.title} corporate AV production`} ratio='4 / 3' policy={MEDIA_POLICY_KEYS.CARD} />
                <div className={styles.caseCopy}>
                  <span>{project.type}</span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <ul aria-label={`${project.title} production facts`}>
                    <li>{project.crew}</li>
                    <li>{project.scope}</li>
                    <li>{project.units}</li>
                  </ul>
                  <Action variant='plain' href={`/portfolio/${project.slug}`} icon={FiArrowRight}>
                    View documented project
                  </Action>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className={styles.processSection} aria-labelledby='corporate-process-title'>
        <Container className={styles.splitLayout}>
          <div className={styles.splitMedia}>
            <Media src={content.process.image} alt={content.process.imageAlt} ratio='4 / 5' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} />
          </div>
          <div className={styles.splitCopy}>
            <SectionHeading id='corporate-process-title' eyebrow={content.process.eyebrow} title={content.process.title} description={content.process.description} />
            <ol className={styles.processList}>
              {content.process.steps.map((step, index) => (
                <li key={step.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Container>
      </section>

      <section className={styles.venue} aria-labelledby='corporate-venue-title'>
        <Container className={styles.venueLayout}>
          <div className={styles.venueCopy}>
            <SectionHeading id='corporate-venue-title' eyebrow={content.venue.eyebrow} title={content.venue.title} description={content.venue.description} />
            <ul>
              {content.venue.items.map(item => <li key={item}><FiCheck aria-hidden='true' /><span>{item}</span></li>)}
            </ul>
            <div className={styles.venueActions}>
              <Action onClick={() => openProposal('corporate_production_venue')} icon={FiArrowRight}>Discuss your venue</Action>
              <Action variant='plain' href='/portfolio' icon={FiArrowRight}>View production portfolio</Action>
            </div>
          </div>
          <Media src={content.venue.image} alt={content.venue.imageAlt} ratio='5 / 4' policy={MEDIA_POLICY_KEYS.SECTION_SPLIT} />
        </Container>
      </section>

      <ReviewProof
        eyebrow='Verified Google feedback'
        heading='Trusted by corporate planners and event teams'
        pagePath='/corporate-event-production'
        initialData={reviewData}
        sectionId='corporate-production-reviews'
      />

      <FAQGroup
        questions={content.faqs}
        eyebrow='Planning questions'
        title='Corporate AV production FAQs'
        intro='Clear answers about scope, venues, breakout rooms, timelines, pricing, and service coverage.'
        headingId='corporate-production-faqs'
      />

      <section className={styles.proposal} aria-labelledby='corporate-proposal-title'>
        <Container className={styles.proposalLayout}>
          <div className={styles.proposalIntro}>
            <SectionHeading id='corporate-proposal-title' eyebrow={content.proposal.eyebrow} title={content.proposal.title} description={content.proposal.description} />
            <div className={styles.proposalNotes}>
              <span><FiClock aria-hidden='true' /> Share what you know now; details can be refined later.</span>
              <span><FiMapPin aria-hidden='true' /> Miami, Fort Lauderdale, and South Florida.</span>
              <span><FiUsers aria-hidden='true' /> Reviewed by a production team, not a generic rental queue.</span>
            </div>
          </div>
          <div className={styles.proposalFormCard}>
            <CorporateProposalForm idPrefix='corporate-proposal-inline' />
          </div>
        </Container>
      </section>

      <section className={styles.builderBand} aria-labelledby='corporate-builder-title'>
        <Container className={styles.builderLayout}>
          <div>
            <span>{content.builder.eyebrow}</span>
            <h2 id='corporate-builder-title'>{content.builder.title}</h2>
            <p>{content.builder.description}</p>
          </div>
          <Action variant='secondary' size='large' icon={FiCalendar} onClick={() => openBuilder('corporate_production_late_builder')}>
            {content.builder.action}
          </Action>
        </Container>
      </section>

      <section className={styles.related} aria-labelledby='corporate-related-title'>
        <Container>
          <SectionHeading id='corporate-related-title' eyebrow={content.related.eyebrow} title={content.related.title} />
          <div className={styles.relatedGrid}>
            {content.related.items.map(item => (
              <article key={item.href}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <Action variant='plain' href={item.href} icon={FiArrowRight}>Explore service</Action>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </PageShell>
  )
}

export default CorporateProductionPage
